<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { FormKit } from '@formkit/vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { companyTypeFlags, companyTypeLabel } from '@/utils/companyDetail'
import type User from '@/types/User'
import type ObjectConfig from '@/types/ObjectConfig'

const props = defineProps<{
  isOpen: boolean
  initialData: any
  listOfUsers: User[]
  compantyTypes: ObjectConfig[]
  companyStatuses: ObjectConfig[]
  companySubStatuses: ObjectConfig[]
  sposobyKonaniaKonatelov: ObjectConfig[]
  konecnyUzivateliaVyhod: ObjectConfig[]
}>()

const emit = defineEmits<{
  (e: 'openChange', value: boolean): void
  (e: 'save', value: any): void
}>()

const isOpen = computed({
  get: () => props.isOpen,
  set: (val: boolean) => emit('openChange', val),
})

const values = ref({ ...props.initialData })

const typeFlags = computed(() => companyTypeFlags(values.value.type))

function handleOpenChange(val: boolean) {
  emit('openChange', val)
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      values.value = { ...props.initialData }
    }
  },
)

function onSubmit() {
  emit('save', { ...values.value })
  emit('openChange', false)
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="handleOpenChange">
    <DialogContent class="max-w-[calc(100%-2rem)] rounded-3xl p-6 sm:max-w-146 sm:p-10">
      <DialogHeader class="gap-2 pe-14 text-start">
        <DialogTitle>Upraviť firmu</DialogTitle>
        <DialogDescription>Upravte údaje spoločnosti.</DialogDescription>
      </DialogHeader>
      <form novalidate @submit="onSubmit">
        <div class="overflow-y-auto max-h-[calc(100vh-4rem)] space-y-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 sm:gap-6">
            <!-- Poznámka - full width -->
            <div class="col-span-full">
              <label for="note" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Poznámka
              </label>
              <FormKit
                type="text"
                id="note"
                name="note"
                v-model="values.note"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Názov spoločnosti / Meno a priezvisko -->
            <div>
              <label
                :for="typeFlags.isFyzos ? 'name_fyzos' : 'name'"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ typeFlags.isFyzos ? 'Meno a priezvisko' : 'Názov spoločnosti' }}
              </label>
              <FormKit
                type="text"
                :id="typeFlags.isFyzos ? 'name_fyzos' : 'name'"
                name="name"
                v-model="values.name"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Typ spoločnosti -->
            <div>
              <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Typ spoločnosti
              </label>
              <FormKit
                type="select"
                id="type"
                name="type"
                v-model="values.type"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-for="typ in compantyTypes" :key="typ.id" :value="typ.id">
                  {{ companyTypeLabel(typ.name, typ.description) }}
                </option>
              </FormKit>
            </div>

            <!-- Stav spoločnosti -->
            <div>
              <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Stav spoločnosti
              </label>
              <FormKit
                type="select"
                id="status"
                name="status"
                v-model="values.status"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-for="status in companyStatuses" :key="status.id" :value="status.id">
                  {{ status.name }} ({{ status.id }})
                </option>
              </FormKit>
            </div>

            <!-- Stav žiadosti -->
            <div>
              <label for="sub_status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Stav žiadosti
              </label>
              <FormKit
                type="select"
                id="sub_status"
                name="sub_status"
                v-model="values.sub_status"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-for="subStatus in companySubStatuses" :key="subStatus.id" :value="subStatus.id">
                  {{ subStatus.name }} ({{ subStatus.id }})
                </option>
              </FormKit>
            </div>

            <!-- Účet spoločnosti (správca) -->
            <div>
              <label for="owner" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Účet spoločnosti (správca)
              </label>
              <FormKit
                type="select"
                id="owner"
                name="owner"
                v-model="values.owner"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option v-if="!listOfUsers?.length">Žiadny klienti sa nenašli</option>
                <option v-for="user in listOfUsers" :key="user?.id" :value="user?.id">
                  {{ user?.first_name }} {{ user?.last_name }}, {{ user?.email }}
                </option>
              </FormKit>
            </div>

            <!-- DIČ -->
            <div>
              <label for="dic" class="block text-sm font-medium text-gray-700 dark:text-gray-300">DIČ</label>
              <FormKit
                type="text"
                id="dic"
                name="dic"
                v-model="values.dic"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- IČ DPH -->
            <div>
              <label for="icdph" class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČ DPH</label>
              <FormKit
                type="text"
                id="icdph"
                name="icdph"
                v-model="values.icdph"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Začiatok oprávnenia -->
            <div>
              <label for="zaciatok_opravnenia" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Začiatok oprávnenia
              </label>
              <FormKit
                type="text"
                id="zaciatok_opravnenia"
                name="zaciatok_opravnenia"
                v-model="values.zaciatok_opravnenia"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- IČO / Rodné číslo -->
            <div>
              <label
                :for="typeFlags.isFyzos ? 'ico_fyzos' : 'ico'"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ typeFlags.isFyzos ? 'Rodné číslo' : 'IČO' }}
              </label>
              <FormKit
                type="text"
                :id="typeFlags.isFyzos ? 'ico_fyzos' : 'ico'"
                name="ico"
                v-model="values.ico"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Je platiteľ DPH? -->
            <div>
              <label for="is_dph" class="block text-sm font-medium text-gray-700 pr-2 dark:text-gray-300">
                Je platiteľ DPH?
              </label>
              <FormKit type="checkbox" id="is_dph" name="is_dph" v-model="values.is_dph" />
            </div>

            <!-- Skryť zo UI -->
            <div>
              <label for="hide_from_ui" class="block text-sm font-medium text-gray-700 pr-2 dark:text-gray-300">
                Skryť zo UI
              </label>
              <FormKit type="checkbox" id="hide_from_ui" name="hide_from_ui" v-model="values.hide_from_ui" />
            </div>
          </div>

          <!-- s.r.o.-only fields -->
          <div v-if="typeFlags.isSro" class="mt-4 space-y-4">
            <div class="flex flex-row gap-6">
              <div class="flex flex-col basis-1/2 gap-2">
                <div>
                  <label for="imanie_vyska" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Základné ímanie
                  </label>
                  <FormKit
                    type="text"
                    number
                    id="imanie_vyska"
                    name="imanie_vyska"
                    v-model="values.imanie_vyska"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  />
                </div>
                <div>
                  <label for="imanie_splatene" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Splatené ímanie
                  </label>
                  <FormKit
                    type="text"
                    number
                    id="imanie_splatene"
                    name="imanie_splatene"
                    v-model="values.imanie_splatene"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  />
                </div>
              </div>
              <div class="flex flex-col basis-1/2 gap-2">
                <div>
                  <label for="sposob_konania_konatelov" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Spôsob konania konateľov
                  </label>
                  <FormKit
                    type="select"
                    id="sposob_konania_konatelov"
                    name="sposob_konania_konatelov"
                    v-model="values.sposob_konania_konatelov"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  >
                    <option v-for="sposob in sposobyKonaniaKonatelov" :key="sposob.id" :value="sposob.id">
                      {{ sposob.name }}
                    </option>
                  </FormKit>
                </div>
                <div>
                  <label for="skk_details" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Spôsob konania – detaily
                  </label>
                  <FormKit
                    type="text"
                    id="skk_details"
                    name="skk_details"
                    v-model="values.skk_details"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  />
                </div>
              </div>
            </div>
            <div class="flex flex-row gap-6">
              <div class="flex flex-col basis-1/2 gap-2">
                <div>
                  <label for="konecny_uzivatelia_vyhod" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Konečný užívateľia výhod
                  </label>
                  <FormKit
                    type="select"
                    id="konecny_uzivatelia_vyhod"
                    name="konecny_uzivatelia_vyhod"
                    v-model="values.konecny_uzivatelia_vyhod"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  >
                    <option v-for="kuv in konecnyUzivateliaVyhod" :key="kuv.id" :value="kuv.id">
                      {{ kuv.name }} ({{ kuv.id }})
                    </option>
                  </FormKit>
                </div>
                <div>
                  <label for="kuv_details" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    KUV – detaily
                  </label>
                  <FormKit
                    type="text"
                    id="kuv_details"
                    name="kuv_details"
                    v-model="values.kuv_details"
                    class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- zivnost/fyzos legacy fields (disabled, opacity-60) -->
          <details
            v-if="typeFlags.isZivnost || typeFlags.isFyzos"
            class="mt-4 border border-gray-200 rounded-lg"
          >
            <summary class="flex items-center justify-between p-3 cursor-pointer list-none hover:bg-gray-50">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                Ďalšie údaje
                <span class="text-xs text-gray-500 ml-1">(iba s.r.o. polia)</span>
              </span>
              <span class="text-xs text-gray-400">prázdne</span>
            </summary>
            <div class="p-4 space-y-4">
              <div class="flex flex-row gap-6">
                <div class="flex flex-col basis-1/2 gap-2">
                  <div>
                    <label for="legacy_imanie_vyska" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Základné ímanie
                    </label>
                    <FormKit
                      type="text"
                      number
                      id="legacy_imanie_vyska"
                      name="imanie_vyska"
                      v-model="values.imanie_vyska"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    />
                  </div>
                  <div>
                    <label for="legacy_imanie_splatene" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Splatené ímanie
                    </label>
                    <FormKit
                      type="text"
                      number
                      id="legacy_imanie_splatene"
                      name="imanie_splatene"
                      v-model="values.imanie_splatene"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    />
                  </div>
                </div>
                <div class="flex flex-col basis-1/2 gap-2">
                  <div>
                    <label for="legacy_sposob_konania" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Spôsob konania konateľov
                    </label>
                    <FormKit
                      type="select"
                      id="legacy_sposob_konania"
                      name="sposob_konania_konatelov"
                      v-model="values.sposob_konania_konatelov"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    >
                      <option v-for="sposob in sposobyKonaniaKonatelov" :key="sposob.id" :value="sposob.id">
                        {{ sposob.name }}
                      </option>
                    </FormKit>
                  </div>
                  <div>
                    <label for="legacy_skk_details" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Spôsob konanie – detaily
                    </label>
                    <FormKit
                      type="text"
                      id="legacy_skk_details"
                      name="skk_details"
                      v-model="values.skk_details"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    />
                  </div>
                </div>
              </div>
              <div class="flex flex-row gap-6">
                <div class="flex flex-col basis-1/2 gap-2">
                  <div>
                    <label for="legacy_kuv" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Konečný užívateľia výhod
                    </label>
                    <FormKit
                      type="select"
                      id="legacy_kuv"
                      name="konecny_uzivatelia_vyhod"
                      v-model="values.konecny_uzivatelia_vyhod"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    >
                      <option v-for="kuv in konecnyUzivateliaVyhod" :key="kuv.id" :value="kuv.id">
                        {{ kuv.name }} ({{ kuv.id }})
                      </option>
                    </FormKit>
                  </div>
                  <div>
                    <label for="legacy_kuv_details" class="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      KUV – detaily
                    </label>
                    <FormKit
                      type="text"
                      id="legacy_kuv_details"
                      name="kuv_details"
                      v-model="values.kuv_details"
                      disabled
                      class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700 opacity-60"
                    />
                  </div>
                </div>
              </div>
            </div>
          </details>
        </div>
        <DialogFooter class="mx-0 mb-0 gap-3 p-0 pt-2 sm:justify-end">
          <div class="flex flex-col-reverse justify-end gap-3 pt-3 sm:flex-row">
            <Button variant="outline" type="button" @click="handleOpenChange(false)">
              Zrušiť
            </Button>
            <Button type="submit">Uložiť</Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>